/**
 * FavoritesManager — persists favorite spec configs and gear combos
 * to localStorage. Each favorite stores enough data to reconstruct
 * the configuration.
 */

export interface FavoriteEntry {
  id: string;
  type: 'spec' | 'gear';
  label: string;
  specSummary: string;
  colorHex: string;
  formatted: Record<string, string>;
  specs?: Record<string, unknown>;
  savedAt: number;
}

const STORAGE_KEY = 'eldrick_favorites';

export class FavoritesManager {
  private favorites: FavoriteEntry[] = [];

  constructor() {
    this.load();
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      this.favorites = raw ? JSON.parse(raw) : [];
    } catch {
      this.favorites = [];
    }
  }

  private save(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.favorites));
  }

  isFavorite(label: string, specSummary: string): boolean {
    return this.favorites.some(f => f.label === label && f.specSummary === specSummary);
  }

  /** Toggle favorite. Returns true if now favorited, false if removed. */
  toggle(entry: Omit<FavoriteEntry, 'id' | 'savedAt'>): boolean {
    const idx = this.favorites.findIndex(
      f => f.label === entry.label && f.specSummary === entry.specSummary,
    );
    if (idx >= 0) {
      this.favorites.splice(idx, 1);
      this.save();
      return false;
    }
    this.favorites.push({
      ...entry,
      id: `fav_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      savedAt: Date.now(),
    });
    this.save();
    return true;
  }

  getAll(): FavoriteEntry[] {
    return [...this.favorites].sort((a, b) => b.savedAt - a.savedAt);
  }

  clear(): void {
    this.favorites = [];
    this.save();
  }
}
